import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { API } from "../App";
import Layout from "../components/Layout";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  Send,
  Bot,
  User,
  Sparkles,
  Loader2,
  MessageCircle,
  BookOpen,
  Ticket,
  RefreshCw,
  Info
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Card } from "../components/ui/card";
import { ScrollArea } from "../components/ui/scroll-area";

const MessageBubble = ({ message, isUser }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`flex gap-3 ${isUser ? "flex-row-reverse" : ""}`}
    >
      <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
        isUser 
          ? "bg-[#425B76] text-white" 
          : "bg-gradient-to-br from-violet-500 to-purple-600 text-white"
      }`}>
        {isUser ? <User className="w-4 h-4" /> : <Bot className="w-4 h-4" />}
      </div>
      <div className={`max-w-[80%] ${isUser ? "text-right" : ""}`}>
        <div className={`rounded-2xl px-4 py-3 ${
          isUser 
            ? "bg-[#425B76] text-white rounded-tr-md" 
            : "bg-white border border-slate-200 text-slate-800 rounded-tl-md shadow-sm"
        }`}>
          <p className="text-sm whitespace-pre-wrap leading-relaxed">{message.content}</p>
        </div>
        {message.context_used && !isUser && (
          <div className="flex gap-2 mt-2 flex-wrap">
            {message.context_used.kb_articles > 0 && (
              <Badge variant="outline" className="text-xs border-emerald-200 text-emerald-600 bg-emerald-50 gap-1">
                <BookOpen className="w-3 h-3" />
                {message.context_used.kb_articles} KB articles
              </Badge>
            )}
            {message.context_used.tickets > 0 && (
              <Badge variant="outline" className="text-xs border-blue-200 text-blue-600 bg-blue-50 gap-1">
                <Ticket className="w-3 h-3" />
                {message.context_used.tickets} tickets
              </Badge>
            )}
          </div>
        )}
        <p className={`text-xs text-slate-400 mt-1 ${isUser ? "text-right" : ""}`}>
          {new Date(message.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        </p>
      </div>
    </motion.div>
  );
};

const TypingIndicator = () => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    className="flex gap-3"
  >
    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
      <Bot className="w-4 h-4 text-white" />
    </div>
    <div className="bg-white border border-slate-200 rounded-2xl rounded-tl-md px-4 py-3 shadow-sm">
      <div className="flex gap-1">
        <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
        <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
        <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
      </div>
    </div>
  </motion.div>
);

const SuggestedQuestions = ({ onSelect }) => {
  const questions = [
    "How do I process a payment to a contractor?",
    "What documents do providers need to submit?",
    "How do I update my invoice details?",
    "What are the payment processing timelines?",
    "How do I add a new contractor to my team?"
  ];

  return (
    <div className="space-y-2">
      <p className="text-sm text-slate-500 mb-3">Try asking:</p>
      <div className="flex flex-wrap gap-2">
        {questions.map((q) => (
          <button
            key={q}
            onClick={() => onSelect(q)}
            className="text-sm px-3 py-2 bg-white border border-slate-200 rounded-lg hover:border-[#425B76] hover:bg-slate-50 transition-all text-slate-600 text-left"
          >
            {q}
          </button>
        ))}
      </div>
    </div>
  );
};

const AIAssistantPage = () => {
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [visitorId] = useState(() => {
    // Get or create visitor ID from localStorage
    let vid = localStorage.getItem("chatbot_visitor_id");
    if (!vid) {
      vid = `visitor_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem("chatbot_visitor_id", vid);
    }
    return vid;
  });
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Load existing session if available
  useEffect(() => {
    const savedSessionId = localStorage.getItem("chatbot_session_id");
    if (savedSessionId) {
      loadSession(savedSessionId);
    }
  }, []);

  const loadSession = async (sid) => {
    try {
      const response = await axios.get(`${API}/chatbot/sessions/${sid}`);
      if (response.data.messages && response.data.messages.length > 0) {
        setSessionId(sid);
        setMessages(response.data.messages);
      }
    } catch (error) {
      // Session not found, start fresh
      localStorage.removeItem("chatbot_session_id");
    }
  };

  const sendMessage = async (messageText = inputValue) => {
    const text = messageText.trim();
    if (!text || isLoading) return;

    setInputValue("");
    setIsLoading(true);

    // Add user message immediately
    const userMessage = {
      message_id: `temp_${Date.now()}`,
      role: "user",
      content: text,
      timestamp: new Date().toISOString()
    };
    setMessages(prev => [...prev, userMessage]);

    try {
      const response = await axios.post(`${API}/chatbot/message`, {
        message: text,
        session_id: sessionId,
        visitor_id: visitorId
      });

      // Save session ID
      if (!sessionId && response.data.session_id) {
        setSessionId(response.data.session_id);
        localStorage.setItem("chatbot_session_id", response.data.session_id);
      }

      // Add AI response
      const aiMessage = {
        message_id: response.data.message_id,
        role: "assistant",
        content: response.data.response,
        timestamp: new Date().toISOString(),
        context_used: response.data.context_used
      };
      setMessages(prev => [...prev, aiMessage]);

    } catch (error) {
      // Error handled silently
      toast.error("Failed to send message. Please try again.");
      // Remove the temporary user message on error
      setMessages(prev => prev.filter(m => m.message_id !== userMessage.message_id));
      setInputValue(text); // Restore the message
    } finally {
      setIsLoading(false);
    }
  };

  const startNewChat = () => {
    setMessages([]);
    setSessionId(null);
    localStorage.removeItem("chatbot_session_id");
    toast.success("Started a new conversation");
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <Layout title="AI Assistant">
      <div className="h-[calc(100vh-140px)] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center shadow-lg shadow-violet-500/25">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="font-semibold text-slate-900">AI Support Assistant</h2>
              <p className="text-xs text-slate-500">Powered by your knowledge base & ticket history</p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={startNewChat}
            className="gap-2 border-slate-200"
            data-testid="new-chat-btn"
          >
            <RefreshCw className="w-4 h-4" />
            New Chat
          </Button>
        </div>

        {/* Chat Container */}
        <Card className="flex-1 flex flex-col overflow-hidden border-slate-200">
          {/* Messages Area */}
          <ScrollArea className="flex-1 p-4">
            <div className="space-y-4">
              {messages.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full py-12">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-violet-100 to-purple-100 flex items-center justify-center mb-4">
                    <MessageCircle className="w-8 h-8 text-violet-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">How can I help you today?</h3>
                  <p className="text-slate-500 text-sm text-center max-w-md mb-6">
                    I can answer questions about TalentDesk, payments, invoicing, contractor management, and more.
                  </p>
                  <SuggestedQuestions onSelect={(q) => sendMessage(q)} />
                </div>
              ) : (
                <>
                  <AnimatePresence>
                    {messages.map((message) => (
                      <MessageBubble
                        key={message.message_id}
                        message={message}
                        isUser={message.role === "user"}
                      />
                    ))}
                  </AnimatePresence>
                  {isLoading && <TypingIndicator />}
                </>
              )}
              <div ref={messagesEndRef} />
            </div>
          </ScrollArea>

          {/* Input Area */}
          <div className="p-4 border-t border-slate-200 bg-slate-50/50">
            <div className="flex gap-3">
              <Textarea
                ref={textareaRef}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyPress}
                placeholder="Type your question..."
                className="min-h-[44px] max-h-[120px] resize-none bg-white"
                disabled={isLoading}
                data-testid="chat-input"
              />
              <Button
                onClick={() => sendMessage()}
                disabled={!inputValue.trim() || isLoading}
                className="bg-[#425B76] hover:bg-[#2D4156] text-white h-auto px-4"
                data-testid="send-message-btn"
              >
                {isLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Send className="w-5 h-5" />
                )}
              </Button>
            </div>
            <div className="flex items-center gap-2 mt-2">
              <Info className="w-3 h-3 text-slate-400" />
              <p className="text-xs text-slate-400">
                Press Enter to send, Shift+Enter for new line
              </p>
            </div>
          </div>
        </Card>
      </div>
    </Layout>
  );
};

export default AIAssistantPage;
